/**
 * The arithmetic behind `WeightTrajectoryChart`, kept separate from the SVG
 * for the same reason `weightPlanChartMath.ts` is split from
 * `WeightPlanChart.tsx`: it can be read — and tested with `node:test` —
 * without wading through markup, and without the test runner having to load
 * a CSS module import transitively through the component file.
 */
import type { TrajectoryPoint, WeightTrajectories } from '@loggy/shared';

import { buildValueDomain } from './chartMath.js';
import type { Domain } from './chartMath.js';

export type { Domain } from './chartMath.js';

/**
 * The x-axis span, taken from `trajectories.domain` — NEVER from the data's
 * own extent. That domain deliberately reaches past the deadline when a
 * gradual pace would not make it in time, and a chart that derived its own
 * span from the plotted points would quietly erase the exact gap it exists
 * to show.
 *
 * Falls back to `[0, 1]` only if the domain itself fails to parse — the same
 * defensive floor `chartMath.timeDomain` gives an empty series, never
 * expected in practice since the domain is always supplied by the caller.
 */
export function trajectoryTimeDomain(domain: { from: string; to: string }): [number, number] {
  const from = Date.parse(domain.from);
  const to = Date.parse(domain.to);
  if (!Number.isFinite(from) || !Number.isFinite(to)) return [0, 1];
  return from <= to ? [from, to] : [to, from];
}

/**
 * A plain linear interpolation from one range to another. Used for both axes:
 * the x-axis maps time onto `[plot.x0, plot.x1]`, and the y-axis maps a value
 * onto `[plot.y1, plot.y0]` (inverted, since SVG y grows downward) — same
 * inversion trick `WeightPlanChart` and `MeasurementChart` each write inline
 * as a closure. Pulled out here so it can be tested once, directly, including
 * the ordering guarantee acceptance criterion 2 depends on: two values fed
 * through the SAME scale preserve their real-world order in pixel space.
 *
 * A degenerate domain (`domainMin === domainMax`) maps everything to the
 * midpoint of the range, rather than dividing by zero.
 */
export function linearScale(
  domainMin: number,
  domainMax: number,
  rangeMin: number,
  rangeMax: number,
): (value: number) => number {
  if (domainMax === domainMin) {
    const mid = (rangeMin + rangeMax) / 2;
    return () => mid;
  }
  return (value: number) => rangeMin + ((value - domainMin) / (domainMax - domainMin)) * (rangeMax - rangeMin);
}

export interface TrajectoryPixel {
  x: number;
  y: number;
}

/**
 * Maps one trajectory line's points to pixel space, sorted by `at` first —
 * the same "never trust the caller's own order" discipline `chartMath.
 * sortByTime` holds `MeasurementChart`'s series to. An empty array maps to an
 * empty array: the caller draws nothing and reserves no extra room, which is
 * the whole contract `projected`, `required` and `drift` rely on when absent.
 */
export function mapTrajectoryPoints(
  points: readonly TrajectoryPoint[],
  xScale: (t: number) => number,
  yScale: (v: number) => number,
): TrajectoryPixel[] {
  return [...points]
    .sort((a, b) => Date.parse(a.at) - Date.parse(b.at))
    .map((point) => ({ x: xScale(Date.parse(point.at)), y: yScale(point.value) }));
}

/**
 * Every real number this chart has to fit on its y-axis: every point across
 * all five lines, plus the target itself, since the target's own horizontal
 * marker is a permanent feature of this chart, never something drawn only if
 * convenient. An empty line (`projected`/`required`/`drift` with nothing to
 * show) contributes nothing here, which is what keeps its absence from
 * changing the axis anyone else is drawn against. `drift` matters most here:
 * it is the one line that can dip BELOW every straight one (a curve that
 * flattens can also sag lower before it flattens), so leaving it out would
 * let its own low point draw outside the plot.
 */
export function trajectoryValues(trajectories: WeightTrajectories): number[] {
  const values: number[] = [
    ...trajectories.actual.map((p) => p.value),
    ...trajectories.projected.map((p) => p.value),
    ...trajectories.healthyFromToday.map((p) => p.value),
    ...trajectories.required.map((p) => p.value),
    trajectories.targetValue,
  ];
  return values;
}

/**
 * The finished y-domain: the union of every value above, padded and floored
 * exactly the way `buildValueDomain` already does for the other two charts —
 * reused rather than re-derived, so "how much padding a small axis gets"
 * stays defined in exactly one place across every chart in this app.
 */
export function trajectoryValueDomain(trajectories: WeightTrajectories, precision: number): Domain {
  return buildValueDomain({ values: trajectoryValues(trajectories), precision });
}

/** Roughly how wide the word "Deadline" is at this chart's label size. Plain
 *  geometry, measured once rather than computed: an SVG text node cannot be
 *  measured before it is drawn, and being a few pixels out only shifts which
 *  side the label takes, never whether it fits. */
const DEADLINE_LABEL_WIDTH = 52;
const DEADLINE_LABEL_GAP = 4;

/**
 * Which side of the deadline line its label goes on.
 *
 * The chart's domain now ENDS at the deadline, so that line sits on the
 * right-hand edge of the plot and a label starting there runs off the page.
 * It flips to the left of the line when the right has no room — and only
 * then, because on the left it overlaps the plot it is describing.
 */
export function deadlineLabelAnchor(
  deadlineX: number,
  plotRight: number,
): { x: number; anchor: 'start' | 'end' } {
  const roomOnTheRight = plotRight - deadlineX;
  return roomOnTheRight >= DEADLINE_LABEL_WIDTH + DEADLINE_LABEL_GAP
    ? { x: deadlineX + DEADLINE_LABEL_GAP, anchor: 'start' }
    : { x: deadlineX - DEADLINE_LABEL_GAP, anchor: 'end' };
}

// ---------------------------------------------------------------------------
// The weekly readout — "what should I weigh in this week", for every line at
// once.
//
// Every line now carries one point a week (see `lineTo` in
// `shared/src/nodes/weightPlan.ts`), which is what makes a per-week answer
// possible at all. What follows is the "nearest point in time, or leave it
// out" rule the brief asks for: never zero, never interpolated.
// ---------------------------------------------------------------------------

const WEEK_MS = 7 * 24 * 60 * 60 * 1000;

/**
 * How far a point may sit from the week being read and still count as that
 * week's answer. Half a week either side: two lines built on their own,
 * slightly offset weekly grids (a baseline dated a Wednesday against a
 * reading dated a Monday) still each report their own nearest point, while a
 * line whose last point is several weeks behind the one being read — a
 * projection that already arrived — falls outside it and is left out rather
 * than repeating its own stale ending forever.
 */
export const WEEKLY_READOUT_TOLERANCE_MS = WEEK_MS / 2;

/**
 * The single point in `points` closest in time to `atMs`, or `null` when
 * either `points` is empty or the closest one still sits further away than
 * `toleranceMs`. Never returns an interpolated value between two points —
 * only ever a point the line actually has.
 */
export function nearestPointNear(
  points: readonly TrajectoryPoint[],
  atMs: number,
  toleranceMs: number,
): TrajectoryPoint | null {
  let best: TrajectoryPoint | null = null;
  let bestDistance = Infinity;
  for (const point of points) {
    const distance = Math.abs(Date.parse(point.at) - atMs);
    if (distance < bestDistance) {
      bestDistance = distance;
      best = point;
    }
  }
  return best !== null && bestDistance <= toleranceMs ? best : null;
}

/** One of the five lines, named the way `ChartLegend` names it — the readout
 *  and the key must always agree on what to call a line. */
export interface NamedTrajectorySeries {
  key: string;
  label: string;
  points: readonly TrajectoryPoint[];
}

export interface TrajectoryReadoutEntry {
  key: string;
  label: string;
  point: TrajectoryPoint;
}

/**
 * The full readout for one hovered or focused week: every named series that
 * has a point near `atMs`, each reporting ITS OWN nearest point, in the same
 * order the series were given. A series with nothing near `atMs` — empty, or
 * with its own nearest point outside `toleranceMs` — is simply absent from
 * the result, never a zero or an interpolated stand-in.
 */
export function weeklyReadoutEntries(
  series: readonly NamedTrajectorySeries[],
  atMs: number,
  toleranceMs: number,
): TrajectoryReadoutEntry[] {
  const entries: TrajectoryReadoutEntry[] = [];
  for (const s of series) {
    const point = nearestPointNear(s.points, atMs, toleranceMs);
    if (point) entries.push({ key: s.key, label: s.label, point });
  }
  return entries;
}

/**
 * Every week between `domain.from` and `domain.to`: the crosshair's own
 * reachable set.
 *
 * This used to be `trajectories.actual`'s own points, which is exactly the
 * owner's complaint — every measured week is necessarily in the past, so the
 * plan and the healthy pace, which run FORWARD, could never be pointed at.
 * Building the grid from the domain the lines are already drawn against
 * (`trajectoryTimeDomain`) instead — never from any one line's own points —
 * makes a week with no measurement yet, or one that has not happened yet,
 * exactly as reachable as a week that already has a reading.
 *
 * One entry per week, starting exactly at `domain.from`, stepping by
 * `WEEK_MS`, and never past `domain.to` — the last entry lands AT OR BEFORE
 * it, the same "however it divides need not be exact" allowance
 * `weeklyMarkerIndices` already makes for `required`'s own weekly grid.
 */
export function weekGridForDomain(domain: { from: string; to: string }): number[] {
  const [minT, maxT] = trajectoryTimeDomain(domain);
  const grid: number[] = [];
  for (let t = minT; t <= maxT; t += WEEK_MS) grid.push(t);
  return grid;
}

/**
 * The timestamp of the `index`-th week of `weekGrid` — the pure half of
 * "moving the keyboard slider changes the reported week". The slider's
 * `activeIndex` is now an index into the WEEK GRID (see `weekGridForDomain`),
 * never into `actual`, which is what makes a future or unmeasured week
 * reachable at all. Out of range, or an empty grid, answers `null` rather
 * than throwing — the same floor `mapTrajectoryPoints` gives an
 * out-of-range read.
 */
export function anchorTimeForIndex(weekGrid: readonly number[], index: number): number | null {
  const t = weekGrid[index];
  return t === undefined ? null : t;
}

// ---------------------------------------------------------------------------
// Weekly markers on the `required` line
//
// Six lines times thirty weeks is a hundred and eighty dots on a chart that
// is never wider than half the page — enough to bury the lines they are
// supposed to mark. Only `required` gets permanent weekly dots: it is
// literally "what the weight SHOULD be, week by week" (see its own doc
// comment above), which is the exact sentence the owner's complaint used —
// every other line still answers the same question, but only through the
// readout above, on request, rather than as a permanent hundred-and-fifty
// extra marks. `required`'s own dots are further thinned by the width
// actually available, so a narrow half-width chart never packs them closer
// than they can be told apart.
// ---------------------------------------------------------------------------

/**
 * Which indices of an evenly-spaced weekly series get a marker, given how
 * much pixel width is actually available. Always keeps the first and last
 * point — the start and end of the plan are the two answers read most often
 * — and thins everything between them so that no two markers land closer
 * than `minSpacingPx`, assuming (as `required`'s own weekly points are) even
 * spacing across the width.
 */
export function weeklyMarkerIndices(
  pointCount: number,
  availableWidthPx: number,
  minSpacingPx: number,
): number[] {
  if (pointCount <= 0) return [];
  if (pointCount === 1) return [0];

  const gapPx = availableWidthPx / (pointCount - 1);
  const stride = gapPx <= 0 ? pointCount - 1 : Math.max(1, Math.ceil(minSpacingPx / gapPx));

  const indices: number[] = [];
  for (let i = 0; i < pointCount; i += stride) indices.push(i);
  const lastIndex = pointCount - 1;
  if (indices[indices.length - 1] !== lastIndex) indices.push(lastIndex);
  return indices;
}

// ---------------------------------------------------------------------------
// Keeping the tooltip on the plot — the vertical half of the problem
//
// `deadlineLabelAnchor` above already solves the horizontal half (left vs.
// right). This chart's tooltip can now list up to six rows instead of one,
// so a point near the plot's own top margin can run a tall tooltip off the
// top of the frame — the exact failure this project has already shipped
// three times.
// ---------------------------------------------------------------------------

/** Roughly how tall the tooltip gets per row it lists, and its fixed header
 *  — measured once rather than read from the DOM, the same reasoning
 *  `DEADLINE_LABEL_WIDTH` above gives: an HTML block cannot be measured
 *  before it paints, and being a few pixels out only changes which side of
 *  the point it opens on, never whether it fits. */
const TOOLTIP_HEADER_HEIGHT_PX = 34;
const TOOLTIP_ROW_HEIGHT_PX = 18;

/**
 * Whether the tooltip opens above the active point (its long-standing
 * default, shared with `WeightPlanChart`) or below it. Flips to below only
 * when the estimated block — which grows with `rowCount` — would clear the
 * plot's own top edge, so a six-line readout anchored near the top margin
 * still has somewhere to go.
 */
export function tooltipVerticalAlign(
  pointY: number,
  plotTop: number,
  rowCount: number,
): 'above' | 'below' {
  const estimatedHeight = TOOLTIP_HEADER_HEIGHT_PX + Math.max(0, rowCount) * TOOLTIP_ROW_HEIGHT_PX;
  return pointY - plotTop < estimatedHeight ? 'below' : 'above';
}
